import React, { useState, useEffect } from 'react';
import { Card, Typography, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import FarmMap from '../components/Farm/FarmMap';
import { getDisplayName } from '../utils/auth';
import { canRecordDeadChicken } from '../constants/permissions';

const { Text, Title } = Typography;

const Home = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [activeZone, setActiveZone] = useState(null);

  const canRecord = canRecordDeadChicken();
  const displayName = getDisplayName(i18n.language);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const res = await fetch('/api/weather');
        const data = await res.json();
        setWeather(data);
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: t('home.weather.error'),
          text: err.message,
          confirmButtonColor: '#8B6914',
        });
      } finally {
        setWeatherLoading(false);
      }
    };

    fetchWeather();
  }, [t]);

  const getWeatherDesc = (code) => {
    if (code === 0) return t('home.weather.desc.sunny');
    if (code <= 3) return t('home.weather.desc.cloudy');
    if (code <= 48) return t('home.weather.desc.foggy');
    if (code <= 67) return t('home.weather.desc.rainy');
    if (code <= 77) return t('home.weather.desc.snowy');
    if (code <= 82) return t('home.weather.desc.shower');
    return t('home.weather.desc.stormy');
  };

  const handleShedClick = (barnId) => {
    navigate(`/barn/${barnId}`);
  };

  return (
    <div className="home-page px-3 sm:px-4 py-4 sm:py-6 max-w-7xl mx-auto space-y-4 sm:space-y-6">
      {displayName && (
        <Card className="bg-white/90 border-0 shadow-sm home-greeting-card">
          <Title level={3} className="!mb-0 !text-wabi-brown-800 text-center sm:text-left">
            {t('home.greeting', { name: displayName })}
          </Title>
        </Card>
      )}

      <Card className="bg-white/90 border-0 shadow-sm">
        {weatherLoading ? (
          <Text className="text-gray-500">{t('home.weather.loading')}</Text>
        ) : weather ? (
          <Space size="middle" wrap className="w-full justify-center home-weather">
            <Text className="text-3xl sm:text-4xl font-bold text-wabi-brown-800">
              {weather.temperature}
              {weather.unit}
            </Text>
            <div>
              <Text className="block text-wabi-brown-600 text-lg">
                {getWeatherDesc(weather.weathercode)}
              </Text>
              <Text className="block text-gray-500 text-sm">
                {t('home.weather.windspeed')} {weather.windspeed} km/h
              </Text>
            </div>
          </Space>
        ) : (
          <Text className="text-gray-500">{t('home.weather.unavailable')}</Text>
        )}
      </Card>

      <FarmMap
        activeZone={activeZone}
        onZoneChange={setActiveZone}
        canRecord={canRecord}
        onShedClick={handleShedClick}
      />
    </div>
  );
};

export default Home;
